import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { mutateImageUrl, convertFileToBase64 } from '../../../wordpress';
import TorsoTextOverlay from '../TorsoTextOverlay';
import TorsoImageUploadOverlay from '../../TorsoImageUploadOverlay';
import DisplayImageUploadOverlay from '../../DisplayImageUploadOverlay';
import { setTorsoImageUpload, setDisplayImageUpload, setDisplayImageUploadDraft } from '../../../actions';
import { getDisplayUploadConfig } from '../../../utils/displayUploadConfig';
import { isCreateYourOwnHeadProductId } from '../../../constants';

const ProductImageView = ({
    defaultImage = null,
    attr = null,
    torsoOverlayImages = {},
    activeSection = '',
    currentProductId,
    selectedProducts = {},
    backImageSrc = '',
    showBackImage = false,
    torsoTextState = null,
    productTags = [],
    product = null,
    variations = [],
    isKeyChain = false,
    headFeatures = null,
    torsoImageUpload = null,
    displayImageUpload = null,
    displayImageUploadDraft = null,
    showImageDevMode = false,
    addonsContainerRef = null,
    // From Redux
    dispatch,
    currentBuildIndex,
}) => {
    const image1FieldObserverRef = useRef(null);
    const image1ChangeHandlerRef = useRef(null);
    const image1InputRef = useRef(null);

    // Observer for BOLD "Image 1" field injection - isolated to this component
    useEffect(() => {
        if (activeSection !== 'bodies' || !addonsContainerRef?.current) {
            return;
        }

        // Create stable handler function
        const handleImageUpload = async (e) => {
            const file = e.target.files?.[0];

            if (file) {
                try {
                    const encodedImage = await convertFileToBase64(file);

                    dispatch(
                        setTorsoImageUpload(currentBuildIndex, {
                            dataUri: encodedImage,
                            compositeDataUri: null,
                            position: { x: 0, y: 0, scale: 1 },
                        })
                    );
                } catch (error) {
                    console.error('Failed to convert image to base64:', error);
                }
            }
        };

        const checkForImage1Field = () => {
            if (!addonsContainerRef.current) return false;

            // If we already have a listener attached, skip
            if (image1InputRef.current && image1ChangeHandlerRef.current) {
                return true;
            }

            // Try multiple selectors
            const selectors = [
                'input[name="properties[Image 1]"]',
                'input[type="file"][name*="Image 1"]',
                'input.uf_1824514_713471',
            ];

            let image1Input = null;

            for (const selector of selectors) {
                image1Input = addonsContainerRef.current.querySelector(selector);
                if (image1Input) {
                    break;
                }
            }

            if (image1Input && image1Input !== image1InputRef.current) {
                // Remove old listener if it exists
                if (image1InputRef.current && image1ChangeHandlerRef.current) {
                    image1InputRef.current.removeEventListener('change', image1ChangeHandlerRef.current);
                }

                // Store refs
                image1InputRef.current = image1Input;
                image1ChangeHandlerRef.current = handleImageUpload;

                // Attach new listener
                image1Input.addEventListener('change', handleImageUpload);

                // Stop observing once found
                if (image1FieldObserverRef.current) {
                    image1FieldObserverRef.current.disconnect();
                    image1FieldObserverRef.current = null;
                }

                return true;
            }

            return false;
        };

        // Check immediately
        if (checkForImage1Field()) {
            return;
        }

        // Check again after a delay (BOLD might inject late)
        const delayedCheck = setTimeout(() => {
            checkForImage1Field();
        }, 1000);

        // Set up observer for BOLD dynamic injection only if field not found yet
        if (!image1FieldObserverRef.current && addonsContainerRef.current) {
            const observer = new MutationObserver(() => {
                if (checkForImage1Field()) {
                    observer.disconnect();
                }
            });

            observer.observe(addonsContainerRef.current, {
                childList: true,
                subtree: true,
            });

            image1FieldObserverRef.current = observer;
        }

        return () => {
            clearTimeout(delayedCheck);

            if (image1FieldObserverRef.current) {
                image1FieldObserverRef.current.disconnect();
                image1FieldObserverRef.current = null;
            }

            // Clean up listener when leaving bodies section
            if (image1InputRef.current && image1ChangeHandlerRef.current) {
                image1InputRef.current.removeEventListener('change', image1ChangeHandlerRef.current);
                image1InputRef.current = null;
                image1ChangeHandlerRef.current = null;
            }
        };
    }, [activeSection, addonsContainerRef, dispatch, currentBuildIndex]);

    const selectedDisplayProductId = selectedProducts.display?.product?.id;
    const isViewingSelectedDisplayProduct = selectedDisplayProductId === currentProductId;

    // ── Display file-input observer (config-driven selectors) ──────────
    const displayUploadConfig = activeSection === 'display' ? getDisplayUploadConfig(currentProductId) : null;
    const displayInputRef = useRef(null);
    const displayHandlerRef = useRef(null);
    const displayObserverRef = useRef(null);

    useEffect(() => {
        if (activeSection !== 'display' || !displayUploadConfig || !addonsContainerRef?.current) {
            return;
        }

        const handleDisplayImageUpload = async (e) => {
            const file = e.target.files?.[0];
            if (file) {
                try {
                    const encodedImage = await convertFileToBase64(file);
                    const setDisplayUpload = isViewingSelectedDisplayProduct
                        ? setDisplayImageUpload
                        : setDisplayImageUploadDraft;
                    dispatch(
                        setDisplayUpload(currentBuildIndex, {
                            dataUri: encodedImage,
                            compositeDataUri: null,
                            productId: currentProductId,
                            position: displayUploadConfig.defaultPosition || { x: 0, y: 0, scale: 1 },
                        })
                    );
                } catch (error) {
                    console.error('Failed to convert display image to base64:', error);
                }
            }
        };

        const checkForDisplayField = () => {
            if (!addonsContainerRef.current) return false;
            if (displayInputRef.current && displayHandlerRef.current) return true;

            const selectors = displayUploadConfig.inputSelectors || [];
            let input = null;
            for (const sel of selectors) {
                input = addonsContainerRef.current.querySelector(sel);
                if (input) break;
            }

            // Fallback: if selectors don't match, bind the first file input.
            if (!input) {
                input = addonsContainerRef.current.querySelector('input[type="file"]');
            }

            if (input && input !== displayInputRef.current) {
                if (displayInputRef.current && displayHandlerRef.current) {
                    displayInputRef.current.removeEventListener('change', displayHandlerRef.current);
                }
                displayInputRef.current = input;
                displayHandlerRef.current = handleDisplayImageUpload;
                input.addEventListener('change', handleDisplayImageUpload);

                if (displayObserverRef.current) {
                    displayObserverRef.current.disconnect();
                    displayObserverRef.current = null;
                }
                return true;
            }
            return false;
        };

        if (checkForDisplayField()) return;

        const delayedCheck = setTimeout(() => checkForDisplayField(), 1000);

        if (!displayObserverRef.current && addonsContainerRef.current) {
            const observer = new MutationObserver(() => {
                if (checkForDisplayField()) observer.disconnect();
            });
            observer.observe(addonsContainerRef.current, { childList: true, subtree: true });
            displayObserverRef.current = observer;
        }

        return () => {
            clearTimeout(delayedCheck);
            if (displayObserverRef.current) {
                displayObserverRef.current.disconnect();
                displayObserverRef.current = null;
            }
            if (displayInputRef.current && displayHandlerRef.current) {
                displayInputRef.current.removeEventListener('change', displayHandlerRef.current);
                displayInputRef.current = null;
                displayHandlerRef.current = null;
            }
        };
    }, [
        activeSection,
        addonsContainerRef,
        dispatch,
        currentBuildIndex,
        currentProductId,
        displayUploadConfig,
        isViewingSelectedDisplayProduct,
    ]);

    const TORSO_UPLOAD_PRODUCT_ID = 'gid://shopify/Product/7841586053294';
    const isTorsoUploadProduct = currentProductId === TORSO_UPLOAD_PRODUCT_ID;

    let imgSrc = '';
    if (showBackImage && backImageSrc) {
        imgSrc = backImageSrc;
    } else if (attr?.image?.url) {
        imgSrc = attr.image.url;
    } else if (defaultImage?.url) {
        imgSrc = defaultImage.url;
    } else {
        imgSrc = '';
    }
    const attributeIsSelected = !(Array.isArray(attr) && attr.length === 0) || variations.length === 1;
    const isViewingSelectedProduct = selectedProducts.bodies?.product?.id === currentProductId;
    let showOverlayText = false;
    if (
        ((selectedProducts.bodies && isViewingSelectedProduct) || !selectedProducts.bodies) &&
        (attributeIsSelected || showBackImage)
    ) {
        showOverlayText = true;
    }

    // The product ID is stable even when the merchant changes its title/handle.
    const isCreateYourOwnHead = isCreateYourOwnHeadProductId(product?.id);
    const shouldShowHeadOverlays = activeSection === 'heads' && isCreateYourOwnHead && headFeatures;
    const facialFeaturesHandle = headFeatures?.facialFeaturesHandle || '';
    const facialFeaturesClassName =
        /freckle/i.test(facialFeaturesHandle) && !/lipstick|kisses?/i.test(facialFeaturesHandle)
            ? 'image-overlay overlay-head--facialFeatures overlay-head--facialFeatures-underHair'
            : 'image-overlay overlay-head--facialFeatures';
    const displayUploadProductId = displayImageUpload?.productId;
    const displayUploadDraftProductId = displayImageUploadDraft?.productId;
    const isDisplayUploadDraftForCurrentProduct =
        !!displayImageUploadDraft?.dataUri && displayUploadDraftProductId === currentProductId;
    const isDisplayUploadForCurrentProduct =
        !!displayImageUpload?.dataUri &&
        (displayUploadProductId ? displayUploadProductId === currentProductId : isViewingSelectedDisplayProduct);
    const displayImageUploadForCurrentProduct = isViewingSelectedDisplayProduct
        ? isDisplayUploadForCurrentProduct
            ? displayImageUpload
            : null
        : isDisplayUploadDraftForCurrentProduct
          ? displayImageUploadDraft
          : null;

    return (
        <div className="product-media product-image-view">
            <img className="base-image" src={mutateImageUrl(imgSrc, 480, 480)} />
            {activeSection === 'bodies' && (
                <>
                    {torsoOverlayImages &&
                        !showBackImage &&
                        !(isTorsoUploadProduct && torsoImageUpload?.compositeDataUri) && (
                            <img
                                className="image-overlay overlay-torso--chest"
                                src={torsoOverlayImages.torsoOverlayImage}
                            />
                        )}

                    {torsoOverlayImages && !showBackImage && !isKeyChain && (
                        <img className="image-overlay overlay-torso--hands" src={torsoOverlayImages.handImage} />
                    )}

                    {showOverlayText && (
                        <TorsoTextOverlay
                            showBackText={showBackImage}
                            torsoTextState={torsoTextState}
                            currentlySelectedAttributes={attr?.id ? attr : null}
                            productTags={productTags}
                            product={product}
                        />
                    )}

                    {!showBackImage && isTorsoUploadProduct && (
                        <TorsoImageUploadOverlay
                            imageData={torsoImageUpload}
                            onCompositeGenerated={(compositeDataUri, position) =>
                                dispatch(
                                    setTorsoImageUpload(currentBuildIndex, {
                                        ...torsoImageUpload,
                                        compositeDataUri,
                                        position,
                                    })
                                )
                            }
                            torsoImageSrc={mutateImageUrl(imgSrc, 480, 480)}
                            showDevMode={showImageDevMode}
                            isProductAddedToBuild={!!selectedProducts.bodies}
                        />
                    )}
                </>
            )}

            {shouldShowHeadOverlays && !showBackImage && (
                <>
                    {headFeatures.eyebrows && (
                        <img className="image-overlay overlay-head--eyebrows" src={headFeatures.eyebrows} alt="" />
                    )}
                    {headFeatures.eyes && (
                        <img className="image-overlay overlay-head--eyes" src={headFeatures.eyes} alt="" />
                    )}
                    {headFeatures.mouth && (
                        <img className="image-overlay overlay-head--mouth" src={headFeatures.mouth} alt="" />
                    )}
                    {headFeatures.facialHair?.styleImageUrl && (
                        <img
                            className={`image-overlay overlay-head--${headFeatures.facialHair.type}`}
                            src={headFeatures.facialHair.styleImageUrl}
                            alt=""
                        />
                    )}
                    {headFeatures.glasses && (
                        <img className="image-overlay overlay-head--glasses" src={headFeatures.glasses} alt="" />
                    )}
                    {headFeatures.facialFeatures && (
                        <img className={facialFeaturesClassName} src={headFeatures.facialFeatures} alt="" />
                    )}
                </>
            )}

            {activeSection === 'display' && displayUploadConfig && (
                <DisplayImageUploadOverlay
                    imageData={displayImageUploadForCurrentProduct}
                    onCompositeGenerated={(compositeDataUri, position) => {
                        const setDisplayUpload = isViewingSelectedDisplayProduct
                            ? setDisplayImageUpload
                            : setDisplayImageUploadDraft;
                        dispatch(
                            setDisplayUpload(currentBuildIndex, {
                                ...(displayImageUploadForCurrentProduct || {}),
                                compositeDataUri,
                                productId: currentProductId,
                                position,
                            })
                        );
                    }}
                    displayImageSrc={mutateImageUrl(
                        imgSrc,
                        displayUploadConfig.canvasSize,
                        displayUploadConfig.canvasSize
                    )}
                    clipPathPoints={displayUploadConfig.clipPath}
                    canvasSize={displayUploadConfig.canvasSize}
                    showDevMode={showImageDevMode}
                    isProductAddedToBuild={isViewingSelectedDisplayProduct}
                />
            )}
        </div>
    );
};

ProductImageView.propTypes = {
    defaultImage: PropTypes.shape({
        url: PropTypes.string,
        altText: PropTypes.string,
    }),
    attr: PropTypes.oneOfType([
        PropTypes.shape({
            id: PropTypes.string,
            image: PropTypes.shape({
                url: PropTypes.string,
                headFeatures: PropTypes.shape({
                    eyebrows: PropTypes.string,
                    eyes: PropTypes.string,
                    mouth: PropTypes.string,
                    facialHair: PropTypes.shape({
                        type: PropTypes.string,
                        style: PropTypes.string,
                        styleImageUrl: PropTypes.string,
                    }),
                }),
                altText: PropTypes.string,
            }),
        }),
        PropTypes.array, // some callers may pass [] before selection
        PropTypes.object, // fallback (variant object shape)
    ]),
    torsoOverlayImages: PropTypes.shape({
        torsoOverlayImage: PropTypes.string,
        handImage: PropTypes.string,
    }),
    activeSection: PropTypes.string,
    currentProductId: PropTypes.string,
    selectedProducts: PropTypes.shape({
        bodies: PropTypes.shape({
            product: PropTypes.shape({ id: PropTypes.string }),
        }),
        display: PropTypes.shape({
            product: PropTypes.shape({ id: PropTypes.string }),
        }),
    }),
    backImageSrc: PropTypes.string,
    showBackImage: PropTypes.bool,
    torsoTextState: PropTypes.shape({
        frontText: PropTypes.string,
        backText: PropTypes.string,
    }),
    productTags: PropTypes.arrayOf(PropTypes.string),
    product: PropTypes.object, // could refine if needed
    variations: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.shape({ id: PropTypes.string })])),
    isKeyChain: PropTypes.bool,
    headFeatures: PropTypes.shape({
        eyebrows: PropTypes.string,
        eyes: PropTypes.string,
        mouth: PropTypes.string,
        facialHair: PropTypes.shape({
            type: PropTypes.string,
            style: PropTypes.string,
            styleImageUrl: PropTypes.string,
        }),
        glasses: PropTypes.string,
        facialFeatures: PropTypes.string,
        facialFeaturesHandle: PropTypes.string,
    }),
    torsoImageUpload: PropTypes.shape({
        dataUri: PropTypes.string,
        position: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
            scale: PropTypes.number,
        }),
    }),
    displayImageUpload: PropTypes.shape({
        dataUri: PropTypes.string,
        compositeDataUri: PropTypes.string,
        productId: PropTypes.string,
        position: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
            scale: PropTypes.number,
        }),
    }),
    displayImageUploadDraft: PropTypes.shape({
        dataUri: PropTypes.string,
        compositeDataUri: PropTypes.string,
        productId: PropTypes.string,
        position: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
            scale: PropTypes.number,
        }),
    }),
    showImageDevMode: PropTypes.bool,
    addonsContainerRef: PropTypes.object,
    // From Redux
    dispatch: PropTypes.func,
    currentBuildIndex: PropTypes.number,
};

const mapStateToProps = (state) => ({
    currentBuildIndex: state.currentBuildIndex,
});

export default connect(mapStateToProps)(ProductImageView);
